import {
  ActionGroup,
  Cell,
  Column,
  DialogContainer,
  Item,
  Link,
  Row,
  TableBody,
  TableHeader,
  TableView,
  Text,
  View,
} from '@adobe/react-spectrum';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';

import { useMediaQuery } from '@exchange-frontends/custom-hooks';

import useAppBuilderAppsList from '../../../../../../actions/myxchngApi/selectors/useAppBuilderAppsList';
import { useStore } from '../../../../store';
import { buildAppListingUrl, formatDate } from '../../../../utils/ECAppsExtensionsHelper';
import ECCardButton from '../buttons/ECCardButton';
import ManageContactPublisherDialog from './ManageContactPublisherDialog';
import AppStatusLight from './common/AppStatusLight';
import ExtensionDetails from './common/ExtensionDetails';
import ProductCell from './common/ProductCell';
import { FormattedCell } from './common/StyledComponents';

export const ColumnTitle = styled.span`
  text-align: left;
  font: normal normal normal 11px/14px adobe-clean;
  letter-spacing: 0.66px;
  color: #6e6e6e;
  opacity: 1;
  white-space: nowrap;
`;

export const AppName = styled(RouterLink)`
  text-align: left;
  font: normal normal bold 16px/21px adobe-clean;
  margin-bottom: 10px;
  display: block;
`;

export const columns = [
  {
    uid: 'application',
    name: 'Application',
    sortable: false,
    maxWidth: '20%',
  },
  {
    uid: 'status',
    name: 'Status',
    sortable: false,
    maxWidth: '20%',
  },
  {
    uid: 'dateAcquired',
    name: 'Date acquired',
    sortable: false,
    maxWidth: '15%',
  },
  {
    uid: 'distribution',
    name: 'Distribution',
    sortable: false,
    maxWidth: '15%',
  },
  {
    uid: 'products',
    name: 'Products',
    sortable: false,
    maxWidth: '15%',
  },
  {
    uid: 'actions',
    name: 'Actions',
    sortable: false,
    maxWidth: '20%',
  },
];
const AppsTable = () => {
  const selectedOrg = useStore((state) => state.selectedOrg);
  const { apps, isLoading } = useAppBuilderAppsList();
  const isMobileSized = useMediaQuery('(max-width: 1050px)');
  const [isOpen, setIsOpen] = useState(false);
  const [currentAppId, setCurrentAppId] = useState(null);
  const onItemAction = (key, appId) => {
    if (key === 'get_support') {
      setIsOpen(true);
      setCurrentAppId(appId);
    }
  };

  /** @param {ReturnType<typeof useAppBuilderAppsList>['apps'][number]} app */
  const getAppManageUrl = (app) => {
    return `/manage/apps/ec/${selectedOrg.code}/ABD/view/${app?.id}`;
  };

  /** @param {ReturnType<typeof useAppBuilderAppsList>['appBuilderApps'][number]} app */
  const redirectToHelpUrl = async (app) => {
    const helpUrl = app?.details?.assets?.find((item) => item?.type === 'document');
    window.open(helpUrl?.url, '_blank');
  };
  /** @param {ReturnType<typeof useAppBuilderAppsList>['apps'][number]} item */
  const getColumnContent = (item, columnKey) => {
    switch (columnKey) {
      case 'application':
        return (
          <>
            <Link isQuiet>
              <AppName to={getAppManageUrl(item)}>{item?.name}</AppName>
            </Link>
            <ExtensionDetails app={item} />
          </>
        );
      case 'status':
        return <AppStatusLight status={item?.status} data-testid="EC_APPS_TABLE_ROW__STATUS" />;
      case 'dateAcquired':
        return (
          <span data-testid="EC_APPS_TABLE_ROW__DATE_ACQUIRED">
            {item?.acquiredOn ? formatDate(item.acquiredOn) : 'N/A'}
          </span>
        );
      case 'distribution':
        return <span data-testid="EC_APPS_TABLE_ROW__DISTRIBUTION">{item?.isPrivate ? 'Private' : 'Public'}</span>;
      case 'products':
        return <ProductCell item={item} />;
      case 'actions':
        return (
          <>
            <FormattedCell $isMobileSized={isMobileSized} style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
              <ECCardButton
                data-testid="EC_APPS_TABLE_ROW__CTA_BUTTON"
                app={item}
                loading={isLoading}
                isReviewButton={item?.status?.toLowerCase() === 'pending'}
                isAdmin={selectedOrg?.role === 'ADMIN'}
              />
              <ActionGroup
                data-testid="EC_APPS_TABLE_ROW__MORE_ACTIONS_BUTTON"
                overflowMode="collapse"
                buttonLabelBehavior="collapse"
                maxWidth="size-350"
                marginTop={isMobileSized ? 'size-100' : 'size-0'}
                marginRight={isMobileSized ? 'size-0' : 'size-100'}
                alignSelf={isMobileSized ? 'center' : 'flex-start'}
                onAction={(key) => onItemAction(key, item.id)}
                disabledKeys={item?.jaeger ? ['view_app_documentation', 'view_app_listing', 'get_support'] : []}
              >
                <Item key="view_app_details" textValue="Manage your app">
                  <Text>
                    <Link variant="secondary" isQuiet>
                      <RouterLink to={getAppManageUrl(item)}>View app details</RouterLink>
                    </Link>
                  </Text>
                </Item>
                {!item?.jaeger && (
                  <Item key="view_app_documentation" textValue="View app documentation">
                    <Text>
                      <Link variant="secondary" isQuiet onPress={() => redirectToHelpUrl(item)}>
                        View app documentation
                      </Link>
                    </Text>
                  </Item>
                )}
                {!item?.jaeger && item?.isRetractedApp === false && (
                  <Item key="view_app_listing" textValue="View listing">
                    <Text>
                      <Link variant="secondary" isQuiet href={buildAppListingUrl(item)} target="_blank">
                        View listing
                      </Link>
                    </Text>
                  </Item>
                )}
                {!item?.jaeger && (
                  <Item key="get_support" textValue="Get support">
                    <Text>Get support</Text>
                  </Item>
                )}
              </ActionGroup>
            </FormattedCell>
          </>
        );
      default:
        return item[columnKey];
    }
  };
  return (
    <>
      <View overflow="auto" width="100%">
        <TableView
          overflowMode="wrap"
          aria-label="Search Apps List View"
          density="spacious"
          minHeight="size-3000"
          maxHeight="70vh"
          width="100%"
          minWidth="800px"
          data-testid="EC_APPS_TABLE"
        >
          <TableHeader columns={columns}>
            {(column) => (
              <Column
                allowsSorting={column.sortable}
                key={column.uid}
                align={column.uid === 'actions' ? 'end' : 'start'}
                maxWidth={column.maxWidth}
              >
                <ColumnTitle>{column.name}</ColumnTitle>
              </Column>
            )}
          </TableHeader>
          <TableBody items={apps}>
            {(item) => (
              <Row key={item.id}>
                {(columnKey) => <Cell key={`${item.id}-${columnKey}`}>{getColumnContent(item, columnKey)}</Cell>}
              </Row>
            )}
          </TableBody>
        </TableView>
      </View>
      <DialogContainer onDismiss={() => setIsOpen(false)}>
        {isOpen && <ManageContactPublisherDialog appId={currentAppId} />}
      </DialogContainer>
    </>
  );
};

export default AppsTable;
